import { type RealtimeChannel, type SupabaseClient } from "@supabase/supabase-js";

import { type Database } from "@fitness-app/supabase";

/**
 * Opens a realtime channel, making sure nothing is left on the socket under that topic.
 *
 * `RealtimeClient.channel(topic)` hands back an EXISTING channel when one with the same
 * topic is still registered, and `RealtimeChannel.on()` throws once a channel is joined or
 * joining. Removal is asynchronous, so detaching the previous channel without awaiting it -
 * which every subscribe thunk used to do - left the old channel registered and joined for a
 * moment. A subscribe landing inside that window got the old channel back from `db.channel()`
 * and blew up on the first listener with "cannot add `postgres_changes` callbacks for
 * realtime:<topic> after `subscribe()`" (TRE-1759).
 *
 * Pass the handle the slot currently holds as `previous`; the same-topic sweep afterwards
 * also catches channels this run never knew about - one left behind by an unsubscribe whose
 * removal is still in flight, or by a subscribe that lost the slot.
 */
export const openRealtimeChannel = async (
  db: SupabaseClient<Database>,
  name: string,
  previous?: RealtimeChannel | null,
): Promise<RealtimeChannel> => {
  if (previous) {
    await db.removeChannel(previous);
  }

  const topic = `realtime:${name}`;

  // `RealtimeClient.channel()` never registers a second channel for a topic it already
  // holds, so in practice there is at most one - but sweep every match rather than the
  // first, so a single leftover cannot put us back where we started.
  for (const stale of db.getChannels().filter((channel) => channel.topic === topic)) {
    await db.removeChannel(stale);
  }

  return db.channel(name);
};
